const PIN_STORAGE_KEY = 'hireology.tabSessionUser';

/**
 * Whether this document has already recorded which user it is running as.
 *
 * Module scope makes this false again on every page load, which is how the first
 * session load of a document is told apart from a later refetch within it.
 */
let hasPinnedThisDocument = false;

/**
 * The identity this document recorded, kept in module scope so it survives a
 * back/forward cache restore along with the rest of the heap.
 *
 * sessionStorage is shared by every document in the tab, so a later document that
 * signs in as somebody else overwrites the stored pin. A restored page would then
 * compare against that new user and conclude nothing had changed.
 */
let identityInThisDocument: string | undefined;

const readPin = (): string | null => {
  try {
    return window.sessionStorage.getItem(PIN_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writePin = (userGlobalId: string): void => {
  try {
    window.sessionStorage.setItem(PIN_STORAGE_KEY, userGlobalId);
  } catch {
    // Private browsing. The in-document check still applies.
  }
};

/**
 * Whether this page was reached with the back or forward button, covering both a
 * back/forward cache restore and an ordinary reload from the HTTP cache.
 *
 * Returns false when the timing entry is unavailable, so an unknown navigation
 * never bounces a legitimate session.
 */
const isBackForwardNavigation = (): boolean => {
  if (typeof performance === 'undefined') return false;

  const [entry] = performance.getEntriesByType('navigation');
  return (
    (entry as PerformanceNavigationTiming | undefined)?.type === 'back_forward'
  );
};

/**
 * Reports whether a freshly loaded session still belongs to the user this tab was
 * running as.
 *
 * The session cookie is shared by every tab, so a tab can be handed a session that
 * now belongs to somebody else. Two cases have to be told apart:
 *
 * - the tab refetches its session and gets a different user — always a swap
 * - the tab loads a page and gets a different user — only a swap when it arrived
 *   by back/forward, because signing in as somebody else in this tab is a forward
 *   navigation and is perfectly legitimate
 *
 * Decision tree:
 * - no user pinned yet -> pin this one, same identity
 * - pinned user matches -> same identity
 * - pinned user differs, first load of a back/forward navigation -> not the same
 * - pinned user differs, first load of any other navigation -> re-pin, same identity
 * - pinned user differs, later in the same document -> not the same
 */
export const isSameSessionIdentity = (userGlobalId: string): boolean => {
  // What this document recorded wins over the tab-wide pin, which a later document
  // may have moved on to somebody else.
  const pinnedUserGlobalId = identityInThisDocument ?? readPin();
  const isFirstLoadOfDocument = !hasPinnedThisDocument;
  hasPinnedThisDocument = true;

  const hasDifferentUser =
    pinnedUserGlobalId !== null &&
    pinnedUserGlobalId !== undefined &&
    pinnedUserGlobalId !== userGlobalId;

  if (
    hasDifferentUser &&
    (!isFirstLoadOfDocument || isBackForwardNavigation())
  ) {
    return false;
  }

  identityInThisDocument = userGlobalId;
  writePin(userGlobalId);
  return true;
};

/**
 * Releases the pinned user so the next session load re-pins. Used when tearing a
 * session down, and to isolate tests from one another.
 */
export const clearSessionIdentity = (): void => {
  hasPinnedThisDocument = false;
  identityInThisDocument = undefined;
  try {
    window.sessionStorage.removeItem(PIN_STORAGE_KEY);
  } catch {
    // Nothing to release.
  }
};

/**
 * Raised when session data comes back for a different user than this tab was
 * running as, so callers can tell a changed identity apart from an expired session.
 */
export class SessionIdentityChangedError extends Error {
  constructor() {
    super('Session data returned a different user than this tab signed in as');
    this.name = 'SessionIdentityChangedError';
  }
}
